import React from 'react';
import { CTAButton } from '../ui/CTAButton';
import { Countdown } from '../ui/Countdown';
const HERO_BASE = 'https://images.weserv.nl/?url=cdn.magicpatterns.com/uploads/hJoBWeJMG3L5mE4YhJupcS/mocap_volleybool.webp';
const HERO_IMG = `${HERO_BASE}&w=320&output=webp&q=50`;
const HERO_SRCSET = [`${HERO_BASE}&w=320&output=webp&q=50 320w`, `${HERO_BASE}&w=480&output=webp&q=60 480w`, `${HERO_BASE}&w=800&output=webp&q=72 800w`].join(', ');
const HERO_SIZES = '(max-width: 640px) 92vw, (max-width: 1024px) 60vw, 500px';
const CHECKOUT_URL = 'https://polonia.recursopack.com/cart/41012708802609:1';
// Inline SVGs replace lucide-react icons in the hero — saves importing the entire
// lucide-react package on the critical path (~15KB gzip before tree-shaking quirks).
const Star = ({
  className = ''
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden data-id="element-145">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" data-id="element-146" />
  </svg>;
const Zap = ({
  className = ''
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden data-id="element-147">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" data-id="element-148" />
  </svg>;
const Shield = ({
  className = ''
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden data-id="element-149">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-150" />
  </svg>;
const CreditCard = ({
  className = ''
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden data-id="element-151">
    <rect x="2" y="5" width="20" height="14" rx="2" data-id="element-152" />
    <line x1="2" y1="10" x2="22" y2="10" data-id="element-153" />
  </svg>;
const AlertTriangle = ({
  className = ''
}: {
  className?: string;
}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden data-id="element-154">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" data-id="element-155" />
    <line x1="12" y1="9" x2="12" y2="13" data-id="element-156" />
    <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-157" />
  </svg>;
export function Hero() {
  return <section className="relative w-full min-h-[90vh] md:min-h-screen bg-gradient-to-b from-cream-bg to-cream-alt pt-12 md:pt-20 pb-12 md:pb-16 px-4 md:px-6 overflow-hidden" data-id="element-158">
      <div className="hidden md:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-159" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-160">
        <div className="flex justify-center mb-6" data-id="element-161">
          <div className="inline-flex items-center justify-center flex-wrap gap-2 bg-[#E63946] text-white px-4 md:px-5 py-2 md:py-2.5 rounded-full shadow-lg font-bold text-xs sm:text-sm md:text-base text-center" data-id="element-162">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" data-id="element-163" />
            <span data-id="element-164">
              UWAGA: Dla nauczycieli WF i trenerów siatkówki, którzy spędzają
              wieczory szukając ćwiczeń i planów lekcji
            </span>
          </div>
        </div>

        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.15] font-display font-bold text-center text-white mb-4 md:mb-6 max-w-4xl mx-auto" data-id="element-165">
          Zaplanuj cały rok w 7 minut — nie w 7 godzin
        </h1>

        <p className="text-base md:text-lg text-center text-[#E8EAF0] mb-8 md:mb-12 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-166">
          Pierwszy w Polsce tak kompletny system „Otwórz i prowadź" dla
          nauczycieli i trenerów siatkówki — z gotowymi ćwiczeniami, rotacjami i
          planami lekcji dla każdego poziomu — od U12 do U18. Odzyskaj swój czas
          i prowadź zajęcia jak prawdziwy profesjonalista.
        </p>

        <div className="flex justify-center mb-6 md:mb-8" data-id="element-167">
          <div className="relative w-full px-4 md:px-0 min-h-[340px] sm:min-h-[420px] md:min-h-[500px] flex items-center justify-center" data-id="element-168">
            <img src={HERO_IMG} srcSet={HERO_SRCSET} sizes={HERO_SIZES} alt="SIATKÓWKA GOTOWA: SYSTEM 365 — Kompletny system roczny" width={800} height={800} decoding="async" loading="eager" fetchPriority="high" className="mx-auto max-w-full h-auto w-full max-h-[340px] sm:max-h-[420px] md:max-h-[500px] object-contain drop-shadow-2xl" data-id="element-169" />
          </div>
        </div>

        <div className="flex flex-col items-center gap-3 mb-8" data-id="element-170">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-center" data-id="element-171">
            <div className="flex gap-1 text-gold-accent" data-id="element-172">
              {[0, 1, 2, 3, 4].map(i => <Star key={i} className="w-5 h-5 drop-shadow" data-id="element-173" />)}
            </div>
            <span className="text-base md:text-lg font-semibold text-white" data-id="element-174">
              4,9/5{' '}
              <span className="text-[#9BA3B5] font-normal block sm:inline text-sm md:text-base mt-1 sm:mt-0" data-id="element-175">
                (ponad 1 200 potwierdzonych opinii)
              </span>
            </span>
          </div>

          <div className="inline-flex items-center gap-2 bg-[#1A3257] border border-[#1E3A5F] text-white px-4 md:px-5 py-1.5 md:py-2 rounded-full shadow-md" data-id="element-176">
            <span className="text-sm md:text-base" data-id="element-177">🔥</span>
            <span className="text-xs sm:text-sm md:text-base font-medium" data-id="element-178">
              67 nauczycieli wybrało ten system dzisiaj
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5 mb-8 w-full px-4 md:px-0" data-id="element-179">
          <a href={CHECKOUT_URL} className="w-full sm:w-auto flex justify-center" data-id="element-180">
            <CTAButton size="large" className="w-full sm:w-auto" data-id="element-181">
              🏐 TAK! Chcę gotowy plan na 40 tygodni — 73 ZŁ
            </CTAButton>
          </a>

          <Countdown data-id="element-182" />

          <div className="flex flex-wrap justify-center gap-3 md:gap-4 text-xs md:text-sm text-[#9BA3B5]" data-id="element-183">
            <span className="flex items-center gap-1" data-id="element-184">
              <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-185" />
              Natychmiastowy dostęp
            </span>
            <span className="flex items-center gap-1" data-id="element-186">
              <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-187" />
              Gwarancja 7 dni
            </span>
            <span className="flex items-center gap-1" data-id="element-188">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-accent" data-id="element-189" />
              Jednorazowa płatność
            </span>
          </div>
        </div>

        <div className="text-center" data-id="element-190">
          <p className="text-white font-medium text-sm md:text-base" data-id="element-191">
            🇵🇱 Zasób nr 1 wybierany przez polskich nauczycieli i trenerów
            siatkówki
          </p>
        </div>
      </div>
    </section>;
}